import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { BrandMediaFile, BrandMediaType, LogoVariant } from 'types'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

export interface Data {
  brand_media_file: BrandMediaFile
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

interface Variables {
  brandMediaType: BrandMediaType.LOGO
  brandVisualKitId: number
  mediaFileId: number
  logoVariant?: LogoVariant
  isPrimaryLogo?: boolean
}

export const createBrandMediaFile = async ({
  workspaceId,
  brandVisualKitId,
  ...variables
}: Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/brand_media_files`, {
    method: 'POST',
    body: {
      media_file_id: variables.mediaFileId,
      brand_media_type: variables.brandMediaType,
      logo_variant: variables.logoVariant,
      is_primary_logo: variables.isPrimaryLogo,
    },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to create brand media file', {
      metaData: {
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useCreateBrandMediaFile = (options?: Options) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  return useMutation<Data, unknown, Variables>(
    'createBrandMediaFile',
    async (variables) => {
      return createBrandMediaFile({ workspaceId: currentWorkspaceId, ...variables })
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits'])
        queryClient.invalidateQueries([
          'workspaces',
          currentWorkspaceId,
          'brand_visual_kits',
          variables.brandVisualKitId,
        ])
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
